import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Typography,
  Row,
  Col,
  Modal,
  Form,
  InputNumber,
  message,
  Tabs,
  Tag,
  Tooltip,
  Divider,
  Switch
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  SettingOutlined,
  DollarOutlined,
  SearchOutlined,
  EyeOutlined,
  LayersOutlined,
  UserOutlined,
  MoneyCollectOutlined
} from '@ant-design/icons';
import axios from 'axios';

// Instance ber-token. Endpoint yang dipakai halaman ini SEBELUMNYA terbuka tanpa
// autentikasi, jadi panggilannya memakai axios polos. Setelah router dikunci
// (24 Okt 2026), tanpa header Authorization semuanya dijawab 401.
const adminApi = axios.create();
adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});


const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;
const { TextArea } = Input;

const MasterProducts = () => {
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [merchantFees, setMerchantFees] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [feeTiers, setFeeTiers] = useState([]);

  // Modal states
  const [productModalVisible, setProductModalVisible] = useState(false);
  const [feeModalVisible, setFeeModalVisible] = useState(false);
  const [feeCalculatorVisible, setFeeCalculatorVisible] = useState(false);
  const [tierModalVisible, setTierModalVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editingProduct, setEditingProduct] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [editingTier, setEditingTier] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    category: null,
    supplier: null,
    search: ''
  });

  const [productForm] = Form.useForm();
  const [feeForm] = Form.useForm();
  const [calculatorForm] = Form.useForm();
  const [tierForm] = Form.useForm();

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchSuppliers();
    fetchMerchants();
    fetchFeeTiers();
    fetchSubcategories(); // Fetch all subcategories on load
  }, []);

  useEffect(() => {
    if (filters.category || filters.supplier || filters.search) {
      fetchProducts();
    }
  }, [filters]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.supplier) params.append('supplier', filters.supplier);

      const response = await adminApi.get(`${process.env.REACT_APP_BASE_URL}/master-products/products?${params}`);
      let data = response.data.data;

      // Apply search filter
      if (filters.search) {
        data = data.filter(item =>
          item.product_name.toLowerCase().includes(filters.search.toLowerCase()) ||
          item.product_code.toLowerCase().includes(filters.search.toLowerCase())
        );
      }

      setProducts(data);
    } catch (error) {
      message.error('Gagal memuat data produk');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      console.log('Fetching categories...');
      const response = await adminApi.get(`${process.env.REACT_APP_BASE_URL}/master-products/categories`);
      console.log('Categories response:', response.data);
      setCategories(response.data.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      message.error('Gagal memuat kategori');
    }
  };

  const fetchSubcategories = async (categoryId) => {
    try {
      if (categoryId) {
        // Fetch subcategories for specific category
        const response = await adminApi.get(`${process.env.REACT_APP_BASE_URL}/master-products/categories/${categoryId}/subcategories`);
        setSubcategories(response.data.data);
      } else {
        // Fetch all subcategories for dropdown
        const response = await adminApi.get(`${process.env.REACT_APP_BASE_URL}/master-products/subcategories`);
        setSubcategories(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      message.error('Gagal memuat sub-kategori');
    }
  };

  const fetchSuppliers = async () => {
    try {
      console.log('Fetching suppliers...');
      const response = await adminApi.get(`${process.env.REACT_APP_BASE_URL}/master-products/suppliers`);
      console.log('Suppliers response:', response.data);
      setSuppliers(response.data.data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      message.error('Gagal memuat supplier');
    }
  };

  const fetchMerchantFees = async (merchantId) => {
    try {
      const response = await adminApi.get(`${process.env.REACT_APP_BASE_URL}/master-products/merchant-fees/${merchantId}`);
      setMerchantFees(response.data.data);
    } catch (error) {
      message.error('Gagal memuat fee merchant');
    }
  };

  const fetchMerchants = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await adminApi.get(`${process.env.REACT_APP_BASE_URL}/users?role=merchant`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setMerchants(response.data.data || []);
    } catch (error) {
      message.error('Gagal memuat data merchant');
    }
  };

  const fetchFeeTiers = async () => {
    try {
      const response = await adminApi.get(`${process.env.REACT_APP_BASE_URL}/master-products/fee-tiers`);
      setFeeTiers(response.data.data || []);
    } catch (error) {
      message.error('Gagal memuat data fee tiers');
    }
  };

  const handleCreateProduct = async (values) => {
    try {
      setLoading(true);
      console.log('Creating product with values:', values);
      const response = await adminApi.post(`${process.env.REACT_APP_BASE_URL}/master-products/products`, values);
      console.log('Product creation response:', response.data);
      message.success('Produk berhasil dibuat');
      setProductModalVisible(false);
      productForm.resetFields();
      fetchProducts();
    } catch (error) {
      console.error('Error creating product:', error);
      console.error('Error response:', error.response?.data);
      message.error(error.response?.data?.message || 'Gagal membuat produk');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProduct = async (values) => {
    try {
      setLoading(true);

      console.log('🔧 Update Product - Original values:', values);
      console.log('🔧 Update Product - Selected Product ID:', selectedProduct?.id);

      // Ensure values are in correct format for API
      const updateData = {
        ...values,
        default_fee_merchant: parseFloat(values.default_fee_merchant) || 0,
        default_fee_system: parseFloat(values.default_fee_system) || 0,
        default_min_fee: parseFloat(values.default_min_fee) || 0,
        default_max_fee: parseFloat(values.default_max_fee) || 0,
        min_amount: parseFloat(values.min_amount) || 0,
        max_amount: parseFloat(values.max_amount) || 999999999,
        processing_time_minutes: parseInt(values.processing_time_minutes) || 0,
        sort_order: parseInt(values.sort_order) || 0,
        // Ensure all optional fields are properly set to null instead of undefined
        description: values.description || null,
        instructions: values.instructions || null,
        subcategory_id: values.subcategory_id || null,
        is_active: values.is_active !== undefined ? values.is_active : true,
      };

      // Remove any undefined values that might still exist
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === undefined) {
          updateData[key] = null;
        }
      });

      console.log('🔧 Update Product - Processed data:', updateData);

      const response = await adminApi.put(`${process.env.REACT_APP_BASE_URL}/master-products/products/${selectedProduct.id}`, updateData);

      console.log('✅ Update Product - Response:', response.data);

      message.success('Produk berhasil diperbarui');
      setProductModalVisible(false);
      productForm.resetFields();
      setSelectedProduct(null);
      setEditingProduct(false);
      fetchProducts();
    } catch (error) {
      console.error('❌ Update Product Error:', error);
      console.error('❌ Update Product Error Response:', error.response?.data);
      message.error(error.response?.data?.message || 'Gagal memperbarui produk');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async (id) => {
    Modal.confirm({
      title: 'Hapus Produk',
      content: 'Apakah Anda yakin ingin menghapus produk ini?',
      okText: 'Ya',
      cancelText: 'Batal',
      okType: 'danger',
      onOk: async () => {
        try {
          await adminApi.delete(`${process.env.REACT_APP_BASE_URL}/master-products/products/${id}`);
          message.success('Produk berhasil dihapus');
          fetchProducts();
        } catch (error) {
          message.error(error.response?.data?.message || 'Gagal menghapus produk');
        }
      }
    });
  };

  const handleEditProduct = (record) => {
    setSelectedProduct(record);
    setEditingProduct(true);

    // Fetch subcategories if category exists
    if (record.category_id) {
      fetchSubcategories(record.category_id);
    }

    productForm.setFieldsValue({
      product_code: record.product_code,
      kode_main: record.kode_main,
      product_name: record.product_name,
      category_id: record.category_id,
      subcategory_id: record.subcategory_id || null,
      supplier_id: record.supplier_id,
      default_fee_merchant: parseFloat(record.default_fee_merchant) || 0,
      default_fee_system: parseFloat(record.default_fee_system) || 0,
      default_fee_type: record.default_fee_type || 'percentage',
      default_min_fee: parseFloat(record.default_min_fee) || 0,
      default_max_fee: parseFloat(record.default_max_fee) || 0,
      min_amount: parseFloat(record.min_amount) || 0,
      max_amount: parseFloat(record.max_amount) || 999999999,
      processing_time_minutes: parseInt(record.processing_time_minutes) || 0,
      description: record.description || null,
      instructions: record.instructions || null,
      sort_order: parseInt(record.sort_order) || 0,
      is_active: record.is_active !== undefined ? record.is_active : true,
      default_jenis_beban: record.default_jenis_beban || 'mitra'
    });
    setProductModalVisible(true);
  };

  const handleCalculateFee = async (values) => {
    try {
      const { product_id, amount } = values;
      const response = await adminApi.get(`${process.env.REACT_APP_BASE_URL}/master-products/calculate-default-fee/${product_id}?amount=${amount}`);

      Modal.info({
        title: 'Hasil Kalkulasi Fee Default',
        width: 500,
        content: (
          <div style={{ marginTop: 16 }}>
            <p><strong>Produk:</strong> {response.data.data.product_name}</p>
            <p><strong>Kode Produk:</strong> {response.data.data.product_code}</p>
            <p><strong>Jumlah Transaksi:</strong> Rp {response.data.data.transaction_amount.toLocaleString()}</p>
            <p><strong>Fee:</strong> Rp {response.data.data.fee_amount.toLocaleString()}</p>
            <p><strong>Jumlah Bersih:</strong> Rp {response.data.data.net_amount.toLocaleString()}</p>
            <p><strong>Tipe Fee:</strong> {response.data.data.fee_type}</p>
            {response.data.data.fee_percentage && (
              <p><strong>Persentase Fee:</strong> {response.data.data.fee_percentage}%</p>
            )}
            {response.data.data.fee_flat && (
              <p><strong>Fee Flat:</strong> Rp {response.data.data.fee_flat.toLocaleString()}</p>
            )}
            <br />
            <p style={{ fontSize: '12px', color: '#666' }}>
              <em>Ini adalah perhitungan fee default produk, bukan fee spesifik merchant.</em>
            </p>
          </div>
        )
      });
    } catch (error) {
      message.error(error.response?.data?.message || 'Gagal menghitung fee default');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getFeeDisplay = (fee, feeType, minFee, maxFee) => {
    if (feeType === 'percentage') {
      return `${(fee * 100).toFixed(2)}%`;
    } else if (feeType === 'flat') {
      return formatCurrency(fee);
    } else {
      return `${formatCurrency(fee)} + ${(fee * 100).toFixed(2)}%`;
    }
  };

  const getFeeRange = (minFee, maxFee) => {
    if (minFee > 0 && maxFee > 0) {
      return `${formatCurrency(minFee)} - ${formatCurrency(maxFee)}`;
    } else if (minFee > 0) {
      return `Min: ${formatCurrency(minFee)}`;
    } else if (maxFee > 0) {
      return `Max: ${formatCurrency(maxFee)}`;
    }
    return '-';
  };

  
  const productColumns = [
    {
      title: 'Kode Produk',
      dataIndex: 'product_code',
      key: 'product_code',
      render: (text) => <Text code>{text}</Text>,
    },
    {
      title: 'Kode Provider',
      dataIndex: 'kode_main',
      key: 'kode_main',
      render: (text) => <Text code style={{ backgroundColor: '#f0f2f5', color: '#1890ff' }}>{text}</Text>,
    },
    {
      title: 'Nama Produk',
      dataIndex: 'product_name',
      key: 'product_name',
      render: (text, record) => (
        <div>
          <div><strong>{text}</strong></div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.supplier_name} ({record.supplier_code})
          </div>
        </div>
      ),
    },
    {
      title: 'Kategori',
      dataIndex: 'category_name',
      key: 'category_name',
      render: (category, record) => (
        <div>
          <div><strong>{category}</strong></div>
          <div style={{ fontSize: '11px', color: '#666' }}>
            ID: {record.category_id}
          </div>
          {record.subcategory_name && (
            <div style={{ fontSize: '12px', color: '#666' }}>
              {record.subcategory_name}
              <span style={{ fontSize: '11px', color: '#999' }}> (ID: {record.subcategory_id})</span>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Supplier',
      dataIndex: 'supplier_name',
      key: 'supplier_name',
      render: (name, record) => (
        <div>
          <div><strong>{name}</strong></div>
          <div style={{ fontSize: '11px', color: '#666' }}>
            ID: {record.supplier_id}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.supplier_code}
          </div>
        </div>
      ),
    },
    {
      title: 'Fee Merchant',
      dataIndex: 'default_fee_merchant',
      key: 'default_fee_merchant',
      render: (fee, record) => (
        <div>
          <div><strong>{getFeeDisplay(fee, record.default_fee_type, record.default_min_fee, record.default_max_fee)}</strong></div>
          <div style={{ fontSize: '11px', color: '#666' }}>
            {getFeeRange(record.default_min_fee, record.default_max_fee)}
          </div>
        </div>
      ),
    },
    {
      title: 'Range Amount',
      key: 'amount_range',
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          <div>{formatCurrency(record.min_amount)}</div>
          <div style={{ color: '#666' }}>s/d</div>
          <div>{record.max_amount >= 999999999 ? '∞' : formatCurrency(record.max_amount)}</div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Aktif' : 'Tidak Aktif'}
        </Tag>
      ),
    },
    {
      title: 'Urutan',
      dataIndex: 'sort_order',
      key: 'sort_order',
      render: (order) => <Text>{order}</Text>,
    },
    {
      title: 'Aksi',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit Produk">
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditProduct(record)}
            />
          </Tooltip>
          <Tooltip title="Hapus Produk">
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteProduct(record.id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>Master Produk Pembayaran</Title>
        <Text type="secondary">
          Kelola semua metode pembayaran dengan konfigurasi fee yang fleksibel untuk setiap merchant
        </Text>
      </div>

      <Tabs defaultActiveKey="products" type="card">
        <TabPane tab={<span><SettingOutlined />Master Products</span>} key="products">
          <Card
            title="Master Produk Pembayaran"
            extra={
              <Space>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setEditingProduct(false);
                    setSelectedProduct(null);
                    productForm.resetFields();
                    setProductModalVisible(true);
                  }}
                >
                  Tambah Produk
                </Button>
                <Button
                  icon={<DollarOutlined />}
                  onClick={() => setFeeCalculatorVisible(true)}
                >
                  Kalkulator Fee
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={fetchProducts}
                />
              </Space>
            }
          >
            {/* Filters */}
            <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
              <Col xs={24} sm={8} md={6}>
                <Input
                  placeholder="Cari produk..."
                  prefix={<SearchOutlined />}
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  allowClear
                />
              </Col>
              <Col xs={24} sm={8} md={6}>
                <Select
                  placeholder="Kategori"
                  style={{ width: '100%' }}
                  value={filters.category}
                  onChange={(value) => setFilters(prev => ({ ...prev, category: value }))}
                  allowClear
                >
                  {categories.map(cat => (
                    <Option key={cat.category_code} value={cat.category_code}>
                      {cat.category_name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={8} md={6}>
                <Select
                  placeholder="Supplier"
                  style={{ width: '100%' }}
                  value={filters.supplier}
                  onChange={(value) => setFilters(prev => ({ ...prev, supplier: value }))}
                  allowClear
                >
                  {suppliers.map(supplier => (
                    <Option key={supplier.supplier_code} value={supplier.supplier_code}>
                      {supplier.supplier_name}
                    </Option>
                  ))}
                </Select>
              </Col>
            </Row>

            <Table
              columns={productColumns}
              dataSource={products}
              rowKey="id"
              loading={loading}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
              }}
              scroll={{ x: 1200 }}
            />
          </Card>
        </TabPane>
      </Tabs>

      {/* Product Create/Edit Modal */}
      <Modal
        title={editingProduct ? 'Edit Produk' : 'Tambah Produk Baru'}
        open={productModalVisible}
        onCancel={() => {
          setProductModalVisible(false);
          productForm.resetFields();
          setSelectedProduct(null);
          setEditingProduct(false);
        }}
        onOk={() => productForm.submit()}
        confirmLoading={loading}
        width={800}
      >
        <Form
          form={productForm}
          layout="vertical"
          onFinish={editingProduct ? handleUpdateProduct : handleCreateProduct}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="product_code"
                label="Kode Produk"
                rules={[{ required: true, message: 'Kode produk wajib diisi!' }]}
              >
                <Input placeholder="e.g., qris-duitku" disabled={editingProduct} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="product_name"
                label="Nama Produk"
                rules={[{ required: true, message: 'Nama produk wajib diisi!' }]}
              >
                <Input placeholder="e.g., QRIS Duitku" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="kode_main"
                label="Kode Provider (kode_main)"
                rules={[{ required: true, message: 'Kode provider wajib diisi!' }]}
                tooltip="Kode asli dari provider/supplier yang digunakan untuk API calls (e.g., SP untuk QRIS Duitku, DA untuk DANA)"
              >
                <Input placeholder="e.g., SP, DA, OV, GP" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="category_id"
                label="Kategori"
                rules={[{ required: true, message: 'Kategori wajib dipilih!' }]}
              >
                <Select
                  placeholder="Pilih kategori"
                  onChange={(value) => {
                    productForm.setFieldsValue({ subcategory_id: null });
                    fetchSubcategories(value);
                  }}
                >
                  {categories.map(cat => (
                    <Option key={cat.id} value={cat.id}>
                      {cat.category_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="subcategory_id"
                label="Sub Kategori"
              >
                <Select placeholder="Pilih sub kategori">
                  {subcategories.map(sub => (
                    <Option key={sub.id} value={sub.id}>
                      {sub.subcategory_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="supplier_id"
                label="Supplier"
                rules={[{ required: true, message: 'Supplier wajib dipilih!' }]}
              >
                <Select placeholder="Pilih supplier">
                  {suppliers.map(supplier => (
                    <Option key={supplier.id} value={supplier.id}>
                      {supplier.supplier_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider>Default Fee Configuration</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="default_fee_type"
                label="Tipe Fee"
                initialValue="percentage"
              >
                <Select>
                  <Option value="flat">Flat Fee</Option>
                  <Option value="percentage">Percentage</Option>
                  <Option value="mixed">Mixed</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="default_fee_merchant"
                label="Fee Merchant"
                rules={[{ required: true, message: 'Fee merchant wajib diisi!' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={0.0001}
                  precision={4}
                  placeholder="0.0150 untuk 1.5%"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="default_min_fee"
                label="Minimum Fee (IDR)"
                initialValue={0}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/\$\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="default_max_fee"
                label="Maximum Fee (IDR)"
                initialValue={0}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/\$\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item shouldUpdate={(prev, curr) => prev.category_id !== curr.category_id} noStyle>
            {({ getFieldValue }) =>
              getFieldValue('category_id') === 1 ? (
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="default_jenis_beban"
                      label="Default Jenis Beban"
                      initialValue="mitra"
                      tooltip="Siapa yang menanggung fee secara default (bisa di-override per fee tier mitra)"
                    >
                      <Select>
                        <Option value="mitra">Mitra (dipotong saldo)</Option>
                        <Option value="user">User (ditambah ke QR)</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              ) : null
            }
          </Form.Item>

          <Divider>Product Configuration</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="min_amount"
                label="Minimum Amount (IDR)"
                initialValue={0}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/\$\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="max_amount"
                label="Maximum Amount (IDR)"
                initialValue={999999999}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/\$\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="processing_time_minutes"
                label="Processing Time (menit)"
                initialValue={0}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  placeholder="0 untuk instant"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="sort_order"
                label="Sort Order"
                initialValue={0}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="is_active"
                label="Status"
                initialValue={true}
              >
                <Select>
                  <Option value={true}>Aktif</Option>
                  <Option value={false}>Tidak Aktif</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Deskripsi"
          >
            <TextArea rows={3} placeholder="Deskripsi produk pembayaran" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Fee Calculator Modal */}
      <Modal
        title="Kalkulator Fee"
        open={feeCalculatorVisible}
        onCancel={() => setFeeCalculatorVisible(false)}
        onOk={() => calculatorForm.submit()}
        width={500}
      >
        <Form
          form={calculatorForm}
          layout="vertical"
          onFinish={handleCalculateFee}
        >
          <Form.Item
            name="product_id"
            label="Produk"
            rules={[{ required: true, message: 'Produk wajib dipilih!' }]}
          >
            <Select placeholder="Pilih produk">
              {products.map(product => (
                <Option key={product.id} value={product.id}>
                  {product.product_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="amount"
            label="Jumlah Transaksi (IDR)"
            rules={[{ required: true, message: 'Jumlah transaksi wajib diisi!' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/\$\s?|(,*)/g, '')}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MasterProducts;